// O firmware do PIR (pivô central) grava os campos abaixo dentro de item.GENERIC
// com esses nomes literais (confirmado em /mnt/fcir/eqp/<id>.txt de um PIR real).
// Não há normalização no backend (sir-prod/src/monitoring/ValveController.js repassa
// o JSON do firmware sem alterar nomes), então o parsing por string é a única opção.

function findGeneric(item, name) {
  if (!item || !item.GENERIC) return null;
  const found = item.GENERIC.find((g) => g.NAME && g.NAME.trim() === name);
  return found ? found.VALUE : null;
}

// STATUS do executor real da agenda do PIR (fw_cir, enum AGN_PIR_* em
// inc/user/agn_pir.h) escrito em /mnt/fcir/agn_pir/<id>.txt. NÃO é a mesma
// numeração de AGN_TIME nem a que foi documentada originalmente na issue
// #198 -- a numeração final ficou a critério de quem implementou o
// executor em C, e diverge da que este arquivo tinha antes (bug real,
// achado em teste ao vivo no cir065: STATUS=9 sendo lido como "PIR
// INDISPONÍVEL" quando na verdade é AGN_PIR_RUNNING=9, o que gerou um
// diagnóstico errado registrado e depois corrigido na fw_cir#199).
// Centralizado aqui porque tanto o card compacto (View.vue) quanto o
// painel da agenda (PivotSchedule.vue) precisam do mesmo rótulo.
export const PIR_SCHEDULE_STATUS_LABELS = {
  1: "EXPIRADA",
  2: "PARADA",
  3: "DIA NÃO SELECIONADO",
  4: "EM ESPERA",
  5: "CONFLITO",
  6: "BLOQUEADA POR HORÁRIO DE PONTA",
  7: "DESABILITADA POR SENSOR",
  8: "PIR INDISPONÍVEL",
  9: "EM EXECUÇÃO",
  10: "FALHA DE SEGURANÇA",
  11: "POSIÇÃO FINAL ATINGIDA",
};

// AGN_PIR_SAFETY_FAULT (fw_cir#208): agenda na janela, parada por falha de
// segurança do PIR até o reconhecimento. O motivo vem em GENERIC ("Falha:").
export const PIR_SCHEDULE_SAFETY_FAULT = 10;

// AGN_PIR_ANGLE_REACHED (fw_cir#211): o pivô chegou à posição final e a
// ocorrência terminou antes da duração; fica parado até o fim da janela. É
// fim normal, não falha. Ângulo e horário vêm em GENERIC "Posição final:".
export const PIR_SCHEDULE_ANGLE_REACHED = 11;

// Status em que a agenda está na janela vigente, rodando ou tentando rodar.
// O 11 entra porque a agenda continua dona da janela (parada de propósito).
export const PIR_SCHEDULE_LIVE_STATUSES = [5, 6, 7, 8, 9, PIR_SCHEDULE_SAFETY_FAULT, PIR_SCHEDULE_ANGLE_REACHED];

// Posição da agenda com FOLLOW_ANGLE (fw_cir#211). Os graus restantes vêm
// prontos do fw_cir e não devem ser recalculados a partir do "Posição:" do
// atuador: o fw_cir lê direto do SPG, só conta o alvo depois de andar 2° e
// usa o sentido da agenda -- a conta daqui divergiria da que decide a parada.
export function pirSchedulePosition(schedule) {
  const target = findGeneric(schedule, "Posição final:");
  const current = findGeneric(schedule, "Posição atual:");
  const remaining = current && current.match(/faltam\s+([\d.,]+°)/);
  return {
    target,
    current,
    remaining: remaining ? remaining[1] : null,
    // Sem SPG o pivô não para: segue pela duração da agenda.
    unavailable: !!current && current.trim().toLowerCase().startsWith("indisponível"),
  };
}

// Falha de segurança da agenda. Com fw_cir#208 vem do próprio status da
// agenda (10), com a causa em GENERIC "Falha:". Com fw_cir anterior a agenda
// seguia "EM EXECUÇÃO" (9) com o PIR em FALHA, e a falha vinha do status do
// próprio PIR (sirv4#465) -- mantido como reserva.
export function pirScheduleSafetyFault(schedule, item) {
  if (!schedule || !PIR_SCHEDULE_LIVE_STATUSES.includes(Number(schedule.STATUS))) return null;
  if (Number(schedule.STATUS) === PIR_SCHEDULE_SAFETY_FAULT) {
    const g = (schedule.GENERIC || []).find((x) => x.NAME && x.NAME.trim() === "Falha:");
    return ((g && g.VALUE) || PIR_SCHEDULE_STATUS_LABELS[PIR_SCHEDULE_SAFETY_FAULT]).toUpperCase();
  }
  const fault = pivotSafetyFault(item);
  return fault ? fault.toUpperCase() : null;
}

// O que o card e a aba Agenda mostram no lugar do status da agenda quando o
// PIR não vai seguir a agenda: em falha espera o reconhecimento, em manual o
// fw_cir mantém o pivô em STOP e ignora as agendas. A falha vem primeiro
// porque pede ação do operador.
export function pirScheduleOverride(schedule, item) {
  const fault = pirScheduleSafetyFault(schedule, item);
  if (fault) {
    return {
      kind: "fault",
      label: fault,
      reason: "O DIR parou o pivô e a agenda só volta a comandar depois que a falha for reconhecida (aba Informações).",
    };
  }
  if (!schedule || !PIR_SCHEDULE_LIVE_STATUSES.includes(Number(schedule.STATUS))) return null;
  if (item && item.MANUAL) {
    return {
      kind: "manual",
      label: "IGNORADA: PIR EM MANUAL",
      reason: "Em manual o pivô fica parado e não obedece às agendas.",
    };
  }
  return null;
}

export function isPivot(item) {
  return !!item && item.MODEL === "PIR";
}

export function pivotAngle(item) {
  const raw = findGeneric(item, "Posição:");
  const n = parseFloat(String(raw || "").replace(",", ".").replace("°", ""));
  return isNaN(n) ? 0 : n;
}

// O firmware manda o texto literal "Falha no sensor" em "Posição:" quando o
// leitor de ângulo do pivô está com defeito — sem esse check, pivotAngle()
// cai no fallback de NaN->0 e a UI mostra "0°" indistinguível de uma posição real.
export function pivotPositionFailed(item) {
  const raw = findGeneric(item, "Posição:");
  return !!raw && String(raw).trim().toLowerCase().includes("falha");
}

// Valor curto para o CARD (ex: "2.9 bar"), sem o "(mín.: X)" que só faz
// sentido no painel de detalhes, onde há espaço pra explicar o número.
export function pivotPressureShort(item) {
  const raw = findGeneric(item, "Pressão:");
  if (!raw) return null;
  return String(raw).split("(")[0].trim();
}

export function pivotPercent(item) {
  const raw = findGeneric(item, "Percentímetro:");
  const n = parseFloat(String(raw || "").replace("%", ""));
  return isNaN(n) ? null : n;
}

export function pivotDirection(item) {
  return findGeneric(item, "Sentido do movimento:");
}

export function pivotReversed(item) {
  return (pivotDirection(item) || "").toLowerCase().includes("revers");
}

export function pivotStatusText(item) {
  return findGeneric(item, "Status:");
}

// "Status: Proteção ativada" é o único lugar onde o fw_cir já concilia
// IN4 + STP/PCF8574 + modo Local numa única fonte de verdade (fw_cir
// pir_local_safety_fault) — ler direto daqui em vez de reimplementar a
// checagem a partir de campos crus como "Retorno de segurança:"/"Retorno
// segurança:", que variam de nome e formato entre versões do firmware.
export function pivotSafetyOk(item) {
  return pivotStatusText(item) !== "Proteção ativada" && !pivotSafetyFault(item);
}

// DIR D024 (fw_cir#204): o DIR aborta e fica em FALHA até um novo RUN ou
// STOP, e o fw_cir não religa sozinho. pir_status_desc() escreve "Falha de
// segurança" seguido da causa ("na partida" / "em movimento") quando a sabe.
// Devolve o texto do firmware para a UI mostrar a causa junto.
export function pivotSafetyFault(item) {
  const status = (pivotStatusText(item) || "").trim();
  return status.toLowerCase().startsWith("falha de segurança") ? status : null;
}

// Comandado (item.OPEN) mas ainda sem o ENABLE: o DIR espera a pressão
// mínima antes de andar. Sem checar isso o card mostrava "AVANÇO" em verde
// com o pivô parado esperando pressão (visto no cir065, mín. 5 bar e 0 bar).
export function pivotPressurizing(item) {
  return (pivotStatusText(item) || "").trim().toLowerCase() === "pressurizando";
}

// D024: dura check_time (até 10 s) depois de cada RUN, antes do movimento.
export function pivotValidating(item) {
  return (pivotStatusText(item) || "").trim() === "Validando segurança";
}

// "Pressão:" vem como "2.9 bar (mín.: 1.5)" (fw_cir, actuator_pir_json):
// o mínimo é o valor em uso no DIR, que o fw_cir sincroniza com o cadastro.
export function pivotPressureMin(item) {
  const raw = findGeneric(item, "Pressão:");
  const m = /mín\.?:\s*([\d.,]+)/i.exec(raw || "");
  if (!m) return null;
  const n = parseFloat(m[1].replace(",", "."));
  return isNaN(n) ? null : n;
}

// "Segurança:" só existe com DIR D024: "OK (validação 5 s, filtro 3 s)".
// null = DIR sem validação de segurança (D023) ou ainda sem leitura.
export function pivotSafetyTimes(item) {
  const raw = findGeneric(item, "Segurança:");
  const m = /validação\s*(\d+)\s*s.*filtro\s*(\d+)\s*s/i.exec(raw || "");
  return m ? { check: Number(m[1]), fault: Number(m[2]) } : null;
}

// Único comando do PIR que continua no painel: só relê o status no DIR.
export function pivotRefreshButton(item) {
  return ((item && item.BUTTONS) || []).find((b) => /^PIR_REFRESH\b/.test(b.COMMAND || "")) || null;
}

export function pivotWorkMode(item) {
  return findGeneric(item, "Modo de funcionamento:");
}

export function pivotCannonStatus(item) {
  return findGeneric(item, "Comando do canhão:");
}

export function pivotOperationMode(item) {
  return findGeneric(item, "Modo de operação:");
}

// Leitura física do sensor de pressão (fw_cir/pir_is_pressurized) — ao
// contrário de item.OPEN, continua válida em modo Local, onde o painel
// aciona a bomba sem passar pelo DIR e out_mask/status não refletem isso.
export function pivotPressurized(item) {
  const raw = findGeneric(item, "Pressurizado:");
  return (raw || "").trim().toUpperCase() === "SIM";
}

// STAT_RF_UNKNOWN (fw_cir, issue #191) -- estado transitório logo após um
// restart do fw_cir, antes de qualquer tentativa real de RF: nem status,
// nem sentido, nem pressão são confiáveis ainda (podem ser de antes do
// restart). Cinza tem prioridade sobre tudo, inclusive alarme de segurança.
//
// item.RF_STAT === 2 era a única checagem até um teste ao vivo (PIVO-41/
// PIVO-60, cliente 3v3.farm:8571) mostrar o card verde mesmo com "Status:"
// = "Aguardando comunicação" -- ou seja, RF_STAT não estava em 2 nesse
// caso real (é o texto de Status do próprio PIR que carrega o sinal, não
// necessariamente o RF_STAT do dispositivo). Checa os dois.
function pivotAwaitingComm(item) {
  if (item.RF_STAT === 2) return true;
  const status = (pivotStatusText(item) || "").toLowerCase();
  return status.includes("aguardando") && status.includes("comunica");
}

export function pivotVariant(item) {
  if (pivotAwaitingComm(item)) return "gray";
  if (!pivotSafetyOk(item)) return "red";
  if (pivotValidating(item) || pivotPressurizing(item)) return "blue";
  // !item.OPEN cobre tanto "parado de fato" quanto "rodando em modo Local,
  // sem o DIR saber" — pivotPressurized(item) distingue os dois casos pela
  // leitura física de pressão, que vale nos dois modos. Sem isso, um pivô
  // pressurizado em Local ficava visualmente idêntico a um parado de verdade.
  if (!item.OPEN) return pivotPressurized(item) ? "blue" : "gray";
  const dir = (pivotDirection(item) || "").toLowerCase();
  if (dir.includes("revers")) return "orange";
  if (dir.includes("avan")) return "green";
  return "green";
}

export function pivotStatusLabel(item) {
  if (pivotAwaitingComm(item)) return (pivotStatusText(item) || "AGUARDANDO COMUNICAÇÃO").toUpperCase();
  if (pivotSafetyFault(item)) return pivotSafetyFault(item).toUpperCase();
  if (!pivotSafetyOk(item)) return "ALARME";
  if (pivotPressurizing(item)) return "PRESSURIZANDO";
  if (!item.OPEN) return (pivotStatusText(item) || "PARADO").toUpperCase();
  const dir = (pivotDirection(item) || "").toLowerCase();
  if (dir.includes("revers")) return "REVERSÃO";
  if (dir.includes("avan")) return "AVANÇO";
  return (pivotStatusText(item) || "ATIVO").toUpperCase();
}

// Selo do rodapé do card: altura pensada para uma linha só, então fica sem
// a causa ("... EM MOVIMENTO"), que já aparece na linha da agenda e no painel.
// O texto completo vai no title do selo.
export function pivotStatusShortLabel(item) {
  if (pivotAwaitingComm(item)) return "SEM COMUNICAÇÃO";
  if (pivotSafetyFault(item)) return "FALHA DE SEGURANÇA";
  if (pivotValidating(item)) return "VALIDANDO";
  return pivotStatusLabel(item);
}

const VARIANT_BG_CLASS = {
  green: "bg-3v3",
  orange: "bg-orange",
  gray: "bg-black-body",
  red: "bg-red-body",
  blue: "bg-pacific-blue",
};

export function pivotBgClass(item) {
  return VARIANT_BG_CLASS[pivotVariant(item)];
}

